import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Pagination,
  Alert,
  Tooltip,
  Fab,
  Stack,
  Badge,
  Divider,
  InputAdornment,
  Skeleton,
  Snackbar,
  Collapse,
  AppBar,
  Toolbar,
  Slide,
  useScrollTrigger,
  Fade,
  Zoom,
  LinearProgress,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  GTranslate as TranslateIcon,
  Language as LanguageIcon,
  Clear as ClearIcon,
  SelectAll as SelectAllIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CloudDownload as CloudDownloadIcon,
  Timeline as TimelineIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  VolumeUp as VolumeUpIcon
} from '@mui/icons-material';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const legacySessionKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';

const stripEmojisForTTS = (str) =>
  str.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\uFE0F]/gu, '').trim();

const langMap = {
  zh: 'zh-CN', pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR',
  de: 'de-DE', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU',
  ar: 'ar-SA', hi: 'hi-IN',
};

const speakText = (text, lang) => {
  const clean = stripEmojisForTTS(text || '');
  if (!clean) return;
  if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); return; }
  const utterance = new SpeechSynthesisUtterance(clean);
  if (lang) utterance.lang = langMap[lang] || lang;
  window.speechSynthesis.speak(utterance);
};

const SavedTranslations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State management
  const [translations, setTranslations] = useState([]);
  const [filteredTranslations, setFilteredTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguagePair, setSelectedLanguagePair] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [dateRange, setDateRange] = useState('all');
  const [selectedTranslations, setSelectedTranslations] = useState(new Set());
  const [revealedTranslations, setRevealedTranslations] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterExpanded, setFilterExpanded] = useState(!isMobile);

  const { user } = useAuth();

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleRevealed = useCallback((translationId) => {
    setRevealedTranslations(prev => {
      const next = new Set(prev);
      if (next.has(translationId)) {
        next.delete(translationId);
      } else {
        next.add(translationId);
      }
      return next;
    });
  }, []);

  // Language pairs for filtering
  const [languagePairs, setLanguagePairs] = useState([]);

  const fetchTranslations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let query = supabase
        .from('saved_translations')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by user_id for authenticated users or session_id for anonymous users
      if (user) {
        query = query.eq('user_id', user.id);
      } else {
        const sessionId = localStorage.getItem('lexory_session_id') || localStorage.getItem(legacySessionKey);
        if (sessionId) {
          query = query.eq('session_id', sessionId);
        }
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Deduplicate translations based on content and language pair
      const deduplicatedData = deduplicateTranslations(data || []);
      
      setTranslations(deduplicatedData);
      
      // Extract unique language pairs for filtering
      const pairs = [...new Set(deduplicatedData.map(t => `${t.source_language}-${t.target_language}`))];
      setLanguagePairs(pairs);
      
      // Calculate stats
      calculateStats(deduplicatedData);
      
      if (deduplicatedData.length === 0) {
        setError('No words yet. Click words while you read and they’ll appear here automatically.');
      }
    } catch (error) {
      console.error('Error fetching translations:', error);
      setError('Couldn’t load your words. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const applyFilters = useCallback(() => {
    let filtered = [...translations];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.original_text.toLowerCase().includes(query) ||
        t.translated_text.toLowerCase().includes(query)
      );
    }

    // Language pair filter
    if (selectedLanguagePair !== 'all') {
      filtered = filtered.filter(t => 
        `${t.source_language}-${t.target_language}` === selectedLanguagePair
      );
    }

    // Favorites filter
    if (showFavoritesOnly) {
      filtered = filtered.filter(t => t.is_favorite);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (dateRange) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      
      filtered = filtered.filter(t => new Date(t.created_at) >= cutoffDate);
    }

    setFilteredTranslations(filtered);
    setCurrentPage(1);
  }, [translations, searchQuery, selectedLanguagePair, showFavoritesOnly, dateRange]);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    setFilterExpanded(!isMobile);
  }, [isMobile]);

  const calculateStats = (data) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const todayCount = data.filter(t => new Date(t.created_at) >= today).length;
    const weekCount = data.filter(t => new Date(t.created_at) >= thisWeek).length;
    const monthCount = data.filter(t => new Date(t.created_at) >= thisMonth).length;
    const favoriteCount = data.filter(t => t.is_favorite).length;
    
    const languageStats = data.reduce((acc, t) => {
      const pair = `${t.source_language} → ${t.target_language}`;
      acc[pair] = (acc[pair] || 0) + 1;
      return acc;
    }, {});

    setStats({
      total: data.length,
      today: todayCount,
      week: weekCount,
      month: monthCount,
      favorites: favoriteCount,
      languages: languageStats
    });
  };

  // Function to deduplicate translations
  const deduplicateTranslations = (translations) => {
    const seen = new Map();
    const deduplicated = [];

    for (const translation of translations) {
      // Create a unique key based on content and language pair
      const key = `${translation.original_text.toLowerCase().trim()}_${translation.translated_text.toLowerCase().trim()}_${translation.source_language}_${translation.target_language}`;
      
      if (!seen.has(key)) {
        seen.set(key, translation);
        deduplicated.push(translation);
      } else {
        // Keep the more recent one (or the favorite one)
        const existing = seen.get(key);
        if (translation.is_favorite && !existing.is_favorite) {
          // Replace with favorite version
          const index = deduplicated.findIndex(t => t.id === existing.id);
          if (index !== -1) {
            deduplicated[index] = translation;
            seen.set(key, translation);
          }
        } else if (new Date(translation.created_at) > new Date(existing.created_at) && !existing.is_favorite) {
          // Replace with more recent version if existing is not favorite
          const index = deduplicated.findIndex(t => t.id === existing.id);
          if (index !== -1) {
            deduplicated[index] = translation;
            seen.set(key, translation);
          }
        }
        // Note: We could delete the duplicate from database here, but for safety we'll just hide it in UI
      }
    }

    return deduplicated;
  };

  // Function to clean up database duplicates (call this manually if needed)
  const cleanupDuplicates = async () => {
    if (!window.confirm('This will remove duplicate translations from your database. Are you sure?')) {
      return;
    }

    try {
      setLoading(true);
      showSnackbar('Cleaning up duplicates...', 'info');

      let query = supabase
        .from('saved_translations')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by user if authenticated, otherwise by session
      if (user) {
        query = query.eq('user_id', user.id);
      } else {
        const sessionId = localStorage.getItem('lexory_session_id') || localStorage.getItem(legacySessionKey);
        if (sessionId) {
          query = query.eq('session_id', sessionId);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      const seen = new Map();
      const duplicatesToDelete = [];

      for (const translation of data) {
        const key = `${translation.original_text.toLowerCase().trim()}_${translation.translated_text.toLowerCase().trim()}_${translation.source_language}_${translation.target_language}`;
        
        if (seen.has(key)) {
          const existing = seen.get(key);
          // Keep the favorite one, or the more recent one
          if (translation.is_favorite && !existing.is_favorite) {
            duplicatesToDelete.push(existing.id);
            seen.set(key, translation);
          } else if (!translation.is_favorite && existing.is_favorite) {
            duplicatesToDelete.push(translation.id);
          } else if (new Date(translation.created_at) > new Date(existing.created_at)) {
            duplicatesToDelete.push(existing.id);
            seen.set(key, translation);
          } else {
            duplicatesToDelete.push(translation.id);
          }
        } else {
          seen.set(key, translation);
        }
      }

      // Delete duplicates from database
      if (duplicatesToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('saved_translations')
          .delete()
          .in('id', duplicatesToDelete);

        if (deleteError) throw deleteError;

        showSnackbar(`Removed ${duplicatesToDelete.length} duplicate translations`, 'success');
        
        // Refresh the list
        await fetchTranslations();
      } else {
        showSnackbar('No duplicates found to clean up', 'info');
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      showSnackbar('Failed to clean up duplicates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteSelectedTranslations = async () => {
    if (selectedTranslations.size === 0) return;
    if (!window.confirm(`Delete ${selectedTranslations.size} selected translations?`)) {
      return;
    }

    const ids = Array.from(selectedTranslations);

    try {
      const { error } = await supabase
        .from('saved_translations')
        .delete()
        .in('id', ids);

      if (error) throw error;

      setTranslations(prev => prev.filter(t => !selectedTranslations.has(t.id)));
      setSelectedTranslations(new Set());

      showSnackbar('Selected translations deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting selected translations:', error);
      showSnackbar('Failed to delete selected translations', 'error');
    }
  };

  const toggleFavorite = async (translationId, currentFavoriteStatus) => {
    try {
      const { error } = await supabase
        .from('saved_translations')
        .update({ is_favorite: !currentFavoriteStatus })
        .eq('id', translationId);

      if (error) throw error;

      // Update local state
      setTranslations(prev => 
        prev.map(t => 
          t.id === translationId 
            ? { ...t, is_favorite: !currentFavoriteStatus }
            : t
        )
      );

      showSnackbar(
        `Translation ${!currentFavoriteStatus ? 'added to' : 'removed from'} favorites`,
        'success'
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showSnackbar('Failed to update favorite status', 'error');
    }
  };

  const copyToClipboard = async (text, successMessage = 'Text copied!') => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar(successMessage, 'success');
    } catch (error) {
      console.error('Failed to copy text:', error);
      showSnackbar('Failed to copy text', 'error');
    }
  };

  const deleteTranslation = async (translationId) => {
    if (!window.confirm('Are you sure you want to delete this translation?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('saved_translations')
        .delete()
        .eq('id', translationId);

      if (error) throw error;

      setTranslations(prev => prev.filter(t => t.id !== translationId));
      setSelectedTranslations(prev => {
        const newSet = new Set(prev);
        newSet.delete(translationId);
        return newSet;
      });

      showSnackbar('Translation deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting translation:', error);
      showSnackbar('Failed to delete translation', 'error');
    }
  };

  // Pagination logic
  const paginatedTranslations = filteredTranslations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTranslations.length / itemsPerPage);

  const exportTranslations = (format) => {
    const dataToExport = selectedTranslations.size > 0 
      ? filteredTranslations.filter(t => selectedTranslations.has(t.id))
      : filteredTranslations;

    if (format === 'csv') {
      const csv = [
        'Original Text,Translated Text,Source Language,Target Language,Date,Favorite',
        ...dataToExport.map(t => 
          `"${t.original_text}","${t.translated_text}","${t.source_language}","${t.target_language}","${new Date(t.created_at).toLocaleDateString()}","${t.is_favorite}"`
        )
      ].join('\n');

      // Add UTF-8 BOM to ensure proper encoding of special characters
      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csv;
      
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'saved-translations.csv';
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(dataToExport, null, 2);
      const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'saved-translations.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleSelectAll = () => {
    if (selectedTranslations.size === filteredTranslations.length) {
      setSelectedTranslations(new Set());
    } else {
      setSelectedTranslations(new Set(filteredTranslations.map(t => t.id)));
    }
  };

  const handleTranslationSelect = (translationId) => {
    const newSelected = new Set(selectedTranslations);
    if (newSelected.has(translationId)) {
      newSelected.delete(translationId);
    } else {
      newSelected.add(translationId);
    }
    setSelectedTranslations(newSelected);
  };

  const formatLanguageName = (code) => {
    const languageNames = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'ar': 'Arabic',
      'hi': 'Hindi',
      'auto': 'Auto-detect'
    };
    return languageNames[code] || code.toUpperCase();
  };

  // Scroll trigger for sticky toolbar
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 3 }} />
          <Grid container spacing={3}>
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Compact Hero ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
          border: '1px solid',
          borderColor: 'rgba(102, 126, 234, 0.15)',
          borderRadius: 3,
          mx: { xs: 2, md: 3 },
          mt: { xs: 1, md: 2 },
          mb: 2,
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, md: 2 }}
        >
          {/* Title + subtitle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <TranslateIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                Your Words
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Words and phrases you discovered while reading
              </Typography>
            </Box>
          </Box>

          {/* Inline stats */}
          <Stack direction="row" spacing={{ xs: 2, md: 3 }} alignItems="center" sx={{ flexShrink: 0 }}>
            {[
              { value: stats.total || 0, label: 'Total' },
              { value: stats.favorites || 0, label: 'Favorites' },
              { value: languagePairs.length, label: 'Languages' },
              { value: stats.week || 0, label: 'This Week' },
            ].map((s) => (
              <Box key={s.label} sx={{ textAlign: 'center', minWidth: 50 }}>
                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1, color: 'primary.main' }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Sticky Action Toolbar */}
      <Slide appear={false} direction="down" in={trigger}>
        <AppBar
          position="fixed"
          sx={{
            top: 0,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 1100,
          }}
        >
          <Toolbar>
            <TranslateIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Your Words
            </Typography>
            <Badge badgeContent={selectedTranslations.size} color="primary" sx={{ mr: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
              >
                {selectedTranslations.size === filteredTranslations.length ? 'Deselect' : 'Select All'}
              </Button>
            </Badge>
            {selectedTranslations.size > 0 && (
              <Tooltip title="Delete selected">
                <IconButton
                  size="small"
                  onClick={deleteSelectedTranslations}
                  sx={{ ml: 0.5, color: 'error.main' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {selectedTranslations.size > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => exportTranslations('csv')}
                sx={{ ml: 1 }}
              >
                Export ({selectedTranslations.size})
              </Button>
            )}
          </Toolbar>
        </AppBar>
      </Slide>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 10 }}>
        {/* ── Toolbar: Filters + Analytics toggle ── */}
        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 2.5, '&:last-child': { pb: isMobile ? 2 : 2.5 } }}>
            {/* Filter row */}
            <Box
              onClick={isMobile ? () => setFilterExpanded((prev) => !prev) : undefined}
              sx={{
                display: isMobile ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
                mb: filterExpanded ? 2 : 0,
              }}
            >
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                <FilterIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                Search & Filter
              </Typography>
              <IconButton size="small">
                {filterExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={!isMobile || filterExpanded}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search translations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1, minWidth: 140 }}
                />

                {/* Language Pair */}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Language Pair</InputLabel>
                  <Select
                    value={selectedLanguagePair}
                    onChange={(e) => setSelectedLanguagePair(e.target.value)}
                    label="Language Pair"
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LanguageIcon sx={{ mr: 1, fontSize: 18 }} />
                        All Languages
                      </Box>
                    </MenuItem>
                    {languagePairs.map(pair => (
                      <MenuItem key={pair} value={pair}>
                        {pair.split('-').map(formatLanguageName).join(' → ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Date Range */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>

                {/* Favorites */}
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showFavoritesOnly}
                      onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                      icon={<BookmarkBorderIcon />}
                      checkedIcon={<BookmarkIcon />}
                      sx={{ '&.Mui-checked': { color: 'error.main' } }}
                    />
                  }
                  label={<Typography variant="body2">Favorites</Typography>}
                  sx={{ m: 0, whiteSpace: 'nowrap' }}
                />

                {/* Actions */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Refresh" arrow>
                    <IconButton
                      size="small"
                      onClick={fetchTranslations}
                      disabled={loading}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clean Duplicates" arrow>
                    <IconButton
                      size="small"
                      onClick={cleanupDuplicates}
                      disabled={loading}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:hover': { borderColor: 'warning.main', color: 'warning.main' } }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Collapse>

            {/* Analytics toggle */}
            <Box
              onClick={() => setShowStats((prev) => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
                mt: 1.5,
                pt: 1.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <TimelineIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                Translation Analytics
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {showStats ? 'Hide' : 'Show'}
                </Typography>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowStats((prev) => !prev); }}>
                  {showStats ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={showStats}>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Recent Activity
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Today</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{stats.today || 0}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">This Month</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{stats.month || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Top Language Pairs
                  </Typography>
                  {Object.entries(stats.languages || {}).slice(0, 3).map(([pair, count]) => (
                    <Box key={pair} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{pair}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Collapse>
            {/* Active Filters */}
            {(searchQuery || selectedLanguagePair !== 'all' || showFavoritesOnly || dateRange !== 'all') && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Filters:
                  </Typography>
                  {searchQuery && (
                    <Chip
                      label={`"${searchQuery}"`}
                      onDelete={() => setSearchQuery('')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {selectedLanguagePair !== 'all' && (
                    <Chip
                      label={selectedLanguagePair.split('-').map(formatLanguageName).join(' → ')}
                      onDelete={() => setSelectedLanguagePair('all')}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {showFavoritesOnly && (
                    <Chip
                      label="Favorites"
                      onDelete={() => setShowFavoritesOnly(false)}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {dateRange !== 'all' && (
                    <Chip
                      label={dateRange}
                      onDelete={() => setDateRange('all')}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Results Summary */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Showing {paginatedTranslations.length} of {filteredTranslations.length} translations
            {selectedTranslations.size > 0 && (
              <Chip
                label={`${selectedTranslations.size} selected`}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          {loading && <LinearProgress sx={{ width: 200 }} />}
        </Box>

        {/* Error Alert */}
        {error && (
          <Fade in>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-message': { fontWeight: 500 }
              }}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Translations List */}
        {filteredTranslations.length === 0 ? (
          <Fade in timeout={800}>
            <Card
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              }}
            >
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 3,
                  bgcolor: 'rgba(255,255,255,0.8)',
                  color: 'text.secondary',
                }}
              >
                <TranslateIcon sx={{ fontSize: 60 }} />
              </Avatar>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                No words yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>
                {translations.length === 0 
                  ? "Start reading and clicking words. Your words and phrases will appear here automatically."
                  : "Try adjusting your search terms or filters to find the translations you're looking for."
                }
              </Typography>
            </Card>
          </Fade>
        ) : (
          <>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ maxHeight: 720 }}>
                <Table stickyHeader size="small" aria-label="Saved translations">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 48 }} />
                      <TableCell padding="checkbox" sx={{ width: 52 }} />
                      <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>
                        {isMobile ? 'Tap to see' : 'Original'}
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>Translation</TableCell>
                          <TableCell sx={{ width: 220, fontWeight: 700 }}>Meta</TableCell>
                        </>
                      )}
                      <TableCell align="right" sx={{ width: 160, fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTranslations.map((translation) => {
                      const selected = selectedTranslations.has(translation.id);
                      const revealed = revealedTranslations.has(translation.id);
                      const languageLabel = `${formatLanguageName(translation.source_language)} → ${formatLanguageName(translation.target_language)}`;
                      const dateLabel = new Date(translation.created_at).toLocaleDateString();

                      return (
                        <TableRow
                          key={translation.id}
                          hover
                          selected={selected}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(103, 126, 234, 0.08) !important',
                            },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleTranslationSelect(translation.id)}
                            />
                          </TableCell>

                          <TableCell padding="checkbox">
                            <Tooltip title={translation.is_favorite ? 'Unfavorite' : 'Favorite'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(translation.id, translation.is_favorite);
                                }}
                              >
                                {translation.is_favorite ? (
                                  <FavoriteIcon sx={{ color: 'error.main' }} fontSize="small" />
                                ) : (
                                  <FavoriteBorderIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>

                          <TableCell
                            onClick={isMobile ? () => toggleRevealed(translation.id) : undefined}
                            sx={{
                              maxWidth: 420,
                              cursor: isMobile ? 'pointer' : 'default',
                              userSelect: isMobile ? 'none' : 'auto',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  speakText(
                                    isMobile
                                      ? (revealed ? translation.translated_text : translation.original_text)
                                      : translation.original_text,
                                    isMobile
                                      ? (revealed ? translation.target_language : translation.source_language)
                                      : translation.source_language
                                  );
                                }}
                                sx={{
                                  p: 0.5,
                                  flexShrink: 0,
                                  color: 'primary.main',
                                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                                  borderRadius: '50%',
                                  boxShadow: '0 0 6px 1px rgba(102, 126, 234, 0.2)',
                                  '&:hover': {
                                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                                    boxShadow: '0 0 10px 3px rgba(102, 126, 234, 0.35)',
                                  },
                                }}
                              >
                                <VolumeUpIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: isMobile && revealed ? 'primary.main' : 'text.primary',
                                  ...(isMobile
                                    ? {
                                        whiteSpace: 'normal',
                                        overflow: 'visible',
                                        textOverflow: 'clip',
                                        lineHeight: 1.35,
                                      }
                                    : {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }),
                                }}
                                title={isMobile
                                  ? (revealed ? translation.translated_text : translation.original_text)
                                  : translation.original_text}
                              >
                                {isMobile
                                  ? (revealed ? translation.translated_text : translation.original_text)
                                  : translation.original_text}
                              </Typography>
                            </Box>
                          </TableCell>

                          {!isMobile && (
                            <>
                              <TableCell
                                sx={{
                                  maxWidth: 420,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      speakText(translation.translated_text, translation.target_language);
                                    }}
                                    sx={{
                                      p: 0.5,
                                      flexShrink: 0,
                                      color: 'primary.main',
                                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                                      borderRadius: '50%',
                                      boxShadow: '0 0 6px 1px rgba(102, 126, 234, 0.2)',
                                      '&:hover': {
                                        bgcolor: 'rgba(102, 126, 234, 0.2)',
                                        boxShadow: '0 0 10px 3px rgba(102, 126, 234, 0.35)',
                                      },
                                    }}
                                  >
                                    <VolumeUpIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: 'primary.main',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                    title={translation.translated_text}
                                  >
                                    {translation.translated_text}
                                  </Typography>
                                </Box>
                              </TableCell>

                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                  <Chip
                                    icon={<LanguageIcon />}
                                    label={languageLabel}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {dateLabel}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </>
                          )}

                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Copy original">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(translation.original_text, 'Original text copied!');
                                  }}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy translation">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(translation.translated_text, 'Translation copied!');
                                  }}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteTranslation(translation.id);
                                  }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 4 }}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 600,
                      },
                    }}
                  />
                </Card>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Enhanced Floating Action Buttons */}
      {filteredTranslations.length > 0 && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
          <Stack spacing={2}>
            <Zoom in timeout={1000}>
              <Tooltip title="Export All Translations" placement="left">
                <Fab
                  color="primary"
                  onClick={() => exportTranslations('csv')}
                  disabled={filteredTranslations.length === 0}
                  sx={{
                    boxShadow: '0 8px 32px rgba(103, 126, 234, 0.4)',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 12px 40px rgba(103, 126, 234, 0.5)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <CloudDownloadIcon />
                </Fab>
              </Tooltip>
            </Zoom>
            <Zoom in timeout={1200}>
              <Tooltip title="Back to Top" placement="left">
                <Fab
                  size="medium"
                  color="secondary"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <KeyboardArrowUpIcon />
                </Fab>
              </Tooltip>
            </Zoom>
          </Stack>
        </Box>
      )}

      {/* Enhanced Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 2000 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 2,
            fontWeight: 500,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SavedTranslations;
